<template>
    <v-app id="app" class="app-class">
        <LiveLamp />

        <v-main class="main-content">
            <NavigationDrawer />
            <div :class="['router-view-container', `${$route.name}-view-container`]">
                <router-view v-slot="{ Component }" class="router-view">
                    <component :is="Component" v-if="$route.meta.noAnimation" />
                    <v-fade-transition v-else mode="out-in">
                        <component :is="Component" />
                    </v-fade-transition>
                </router-view>
            </div>
        </v-main>
        <BackgroundComponent v-if="showCoolBackground" />
    </v-app>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue3-toastify';
import _ from 'lodash';

// Stores
import { useUserStore } from '@/stores/user';

// Components
import NavigationDrawer from '@/components/navigation/NavigationDrawer.vue';
import InvitationsComponent from './components/user/invitation/InvitationsComponent.vue';
import BackgroundComponent from '@/components/BackgroundComponent.vue';
import LiveLamp from '@/components/utils/bits/LiveLamp.vue';

const userStore = useUserStore();
const { t } = useI18n();
const showCoolBackground = ref(false);

// Computed properties
const invites = computed(() => ({
    expertInvites: userStore.get_expert_invites,
    groupInvites: userStore.get_group_invites,
    workspaceInvites: userStore.get_workspace_invites,
}));

// Methods
const create_invitation_toast = (invite, invite_type) => {
    toast(InvitationsComponent, {
        data: {
            type: invite_type,
            invite: invite,
            translateFunction: t,
        },
        closeOnClick: false,
        autoClose: false,
        position: toast.POSITION.BOTTOM_RIGHT,
    });
};

// Watch
watch(invites, (currentInvites, previousInvites) => {
    const inviteTypes = ['expertInvites', 'groupInvites', 'workspaceInvites'];

    inviteTypes.forEach((type) => {
        const newInvites = _.differenceBy(currentInvites[type], previousInvites[type], 'id');
        newInvites.forEach((invite) =>
            create_invitation_toast(invite, type.replace('Invites', '')),
        );
    });
});

// Lifecycle hooks
onMounted(() => {
    // change favicon if localhost -> grayscale, test --> green otherwise default
    const favicon = document.getElementById('favicon');
    if (window.location.hostname === 'localhost') {
        favicon.href = '/favicon-dev.ico';
    } else if (window.location.hostname === 'expert-test.respeak.io') {
        favicon.href = '/favicon-test.ico';
    }

    // make keyboard shortcut with ctrl shift alt c
    document.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.shiftKey && e.altKey && (e.key === 'J' || e.key === 'ı')) {
            showCoolBackground.value = !showCoolBackground.value;
        }
    });
});
</script>

<style lang="scss">
@use './assets/main.scss';

.app-class {
    position: fixed;
    min-width: 100dvw;
}

.main-content {
    height: calc(100vh - var(--v-layout-top));
    position: relative;
    display: flex;
    flex-direction: row;
    z-index: 1;
    width: 100%;
    max-width: 100vw !important;
}

$router-view-margin-top: 6px;
$router-view-margin-bottom: 6px;

.router-view-container {
    --left-padding: 8px;
    --right-padding: 6px;

    position: relative;
    width: 100%;
    margin-top: $router-view-margin-top;
    margin-bottom: $router-view-margin-bottom;
    margin-right: auto;
    margin-left: auto;
    padding-left: var(--left-padding);
    padding-right: var(--right-padding);
    height: calc(100vh - #{$router-view-margin-top} - #{$router-view-margin-bottom});

    @media (max-width: 768px) {
        --left-padding: 8px;
        --right-padding: 8px;
    }
}

.router-view {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    background-color: #fbfafd;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    padding: 16px;
}

.widget-expert-view-container {
    margin-top: 8px;
    .router-view {
        background-color: #fbfafd;
        border: 1px solid #e0e0e0;
        height: auto;

        &.fullscreen {
            height: 99%;
        }
    }
}
</style>
